import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, Shield, CheckCircle2, XCircle, Clock, User, FileText } from 'lucide-react';
import ingestionService from '../api';

const ACTION_STYLE = {
  approved: { icon: CheckCircle2, cls: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  rejected: { icon: XCircle, cls: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  pending: { icon: Clock, cls: 'text-yellow-500', bg: 'bg-yellow-50 border-yellow-200' },
};

function AuditEntry({ log }) {
  const action = (log.action || log.status || '').toLowerCase();
  const style = ACTION_STYLE[action] || { icon: Shield, cls: 'text-gray-400', bg: 'bg-gray-50 border-gray-200' };
  const Icon = style.icon;
  const date = log.created_at || log.timestamp || log.reviewed_at;
  const dateStr = date ? new Date(date).toLocaleString() : '—';

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${style.bg}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.cls}`} />
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-800">
            {log.cluster_id ? `Cluster #${log.cluster_id}` : log.document_filename || log.subject || '—'}
          </span>
          <span className={`text-xs font-medium ${style.cls}`}>{action || 'action'}</span>
        </div>
        {(log.document_filename || log.filename) && log.cluster_id && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <FileText className="w-3 h-3" />{log.document_filename || log.filename}
          </div>
        )}
        {log.rationale && (
          <p className="text-xs text-gray-600 italic">"{log.rationale}"</p>
        )}
        <div className="flex items-center gap-3 text-xs text-gray-400 pt-0.5">
          {log.reviewed_by && (
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{log.reviewed_by}</span>
          )}
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{dateStr}</span>
        </div>
      </div>
    </div>
  );
}

export default function GovernanceView() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ingestionService.getAuditLog(null, limit);
      const arr = Array.isArray(data?.logs) ? data.logs : Array.isArray(data) ? data : [];
      setLogs(arr);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const approvedCount = logs.filter(l => (l.action || l.status || '').toLowerCase() === 'approved').length;
  const rejectedCount = logs.filter(l => (l.action || l.status || '').toLowerCase() === 'rejected').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Governance Audit Trail</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {logs.length} decision{logs.length !== 1 ? 's' : ''} recorded
            {logs.length > 0 && ` · ${approvedCount} approved · ${rejectedCount} rejected`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
          >
            {[25, 50, 100, 200].map(n => (
              <option key={n} value={n}>Show {n}</option>
            ))}
          </select>
          <button onClick={loadLogs} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {logs.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Decisions', value: logs.length, cls: 'text-gray-900' },
            { label: 'Approved', value: approvedCount, cls: 'text-green-600' },
            { label: 'Rejected', value: rejectedCount, cls: 'text-red-500' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className={`text-lg font-bold ${s.cls}`}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500">Loading audit trail…</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          Failed to load: {error}
          <button onClick={loadLogs} className="ml-3 underline">Retry</button>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Shield className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No governance decisions recorded yet.</p>
          <p className="text-xs mt-1">Review duplicate clusters in the Duplicates tab to generate audit entries.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => (
            <AuditEntry key={log.id || i} log={log} />
          ))}
        </div>
      )}
    </div>
  );
}
